import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useLanguage, SUPPORTED_LANGUAGES } from "@/contexts/LanguageContext";

// Navigation items with translation keys
const getNavItems = (t) => [
  {
    name: t('nav_workstations'),
    slug: "workstations",
    hasSubmenu: true,
    submenu: [
      { name: t('nav_temporary_desk'), slug: "temporary-desk-setup" },
      { name: t('nav_hotel_room'), slug: "hotel-room-setup" },
      { name: t('nav_compact_workstations'), slug: "compact-workstations" },
      { name: t('nav_shared_spaces'), slug: "shared-spaces" },
      { name: t('nav_on_the_move'), slug: "on-the-move-setup" },
      { name: t('nav_dual_monitor'), slug: "dual-monitor-laptop-setup" },
      { name: t('nav_standing_flexible'), slug: "standing-flexible-workstations" },
    ],
    featured: {
      title: "Ultimate Remote Work Setup Guide",
      slug: "ultimate-remote-work-setup-guide",
      image: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=400&q=80"
    }
  },
  { 
    name: t('nav_connectivity'), 
    slug: "connectivity", 
    hasSubmenu: true,
    submenu: [
      { name: "5G Mobile Routers", slug: "mobile-routers" },
      { name: "eSIM Solutions", slug: "connectivity-tools" },
      { name: "WiFi Hotspots", slug: "portable-wifi-hotspots" },
      { name: "Satellite Internet", slug: "satellite-internet" },
    ],
    featured: {
      title: "Best 5G Routers 2026",
      slug: "miglior-router-5g-portatile-2026-confronto",
      image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=400&q=80"
    }
  },
  { 
    name: t('nav_displays'), 
    slug: "displays", 
    hasSubmenu: true,
    submenu: [
      { name: "Portable Monitors", slug: "travel-monitors" },
      { name: "USB-C Displays", slug: "displays" },
    ],
    featured: {
      title: "Best Portable Monitors 2026",
      slug: "miglior-monitor-portatile-2026-confronto",
      image: "https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=400&q=80"
    }
  },
  { 
    name: t('nav_power'), 
    slug: "power", 
    hasSubmenu: true,
    submenu: [
      { name: "Power Stations", slug: "portable-power-stations" },
      { name: "Solar Panels", slug: "solar-panels" },
      { name: "Power Banks", slug: "power-banks" },
    ],
    featured: {
      title: "Best Power Stations 2026",
      slug: "miglior-power-station-portatile-2026-confronto",
      image: "https://images.unsplash.com/photo-1697824450092-aa6ca0499436?w=400&q=80"
    }
  },
  { 
    name: t('nav_travel_setup'), 
    slug: "travel-setup", 
    hasSubmenu: true,
    submenu: [
      { name: "Laptop Stands", slug: "ergonomic-laptop-stands" },
      { name: "Travel Mice", slug: "travel-mice" },
      { name: "Cooling Pads", slug: "laptop-cooling-pads" },
    ],
    featured: {
      title: "Minimalist Remote Work Kit",
      slug: "minimalist-remote-work-kit",
      image: "https://images.unsplash.com/photo-1621743018966-29194999d736?w=400&q=80"
    }
  },
  { 
    name: t('nav_accessories'), 
    slug: "accessories", 
    hasSubmenu: true,
    submenu: [
      { name: "Keyboards", slug: "portable-keyboards" },
      { name: "Headphones", slug: "noise-cancelling-headphones" },
      { name: "Webcams", slug: "compact-webcams" },
      { name: "Desk Lighting", slug: "led-desk-lighting" },
      { name: "E-Ink Tablets", slug: "e-ink-tablets" },
      { name: "Privacy & Security", slug: "privacy-security-hardware" },
    ],
    featured: {
      title: "Best Travel Keyboards",
      slug: "best-travel-keyboards-remote-work",
      image: "https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=400&q=80"
    }
  },
  { name: t('nav_guides'), slug: "guides", hasSubmenu: false },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  
  // Get translated nav items
  const mainNavItems = getNavItems(t);

  const isActive = (path) => location.pathname === path || location.pathname === `/${language}${path}`;

  // Build language-aware link
  const buildLink = (path) => {
    if (language === 'en') return path;
    return `/${language}${path}`;
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(buildLink(`/blog?search=${encodeURIComponent(searchQuery.trim())}`));
      setSearchOpen(false);
      setSearchQuery("");
      setMobileMenuOpen(false);
    }
  };

  // Handle language change with URL update
  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setLangSheetOpen(false);
    setMobileMenuOpen(false);
    
    // Update URL with new language prefix
    const currentPath = location.pathname;
    let newPath = currentPath;
    
    // Remove existing language prefix
    for (const lang of SUPPORTED_LANGUAGES) {
      if (currentPath.startsWith(`/${lang.code}/`)) {
        newPath = currentPath.substring(lang.code.length + 1);
        break;
      } else if (currentPath === `/${lang.code}`) {
        newPath = '/';
        break;
      }
    }
    
    // Add new language prefix (except for English which is default)
    if (langCode !== 'en') {
      newPath = `/${langCode}${newPath === '/' ? '' : newPath}`;
    }
    
    navigate(newPath || '/');
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setLangSheetOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header 
        className="sticky top-0 z-50 w-full border-b border-[#1A2B3C]/5 bg-[#F9F7F2]/95 backdrop-blur-xl"
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-[68px] items-center justify-between">
            {/* Logo + Brand Name - ALWAYS VISIBLE */}
            <Link 
              to={buildLink("/")}
              className="flex items-center gap-3 min-w-0 flex-shrink-0"
              data-testid="logo-link"
            >
              <img 
                src="/logo.png" 
                alt="NOMAD LIVING LAB" 
                className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0"
              />
              <div className="flex flex-col min-w-0">
                <span className="font-['Libre_Baskerville'] text-base sm:text-lg md:text-xl font-bold text-[#1A2B3C] leading-tight truncate">
                  NOMAD
                </span>
                <span className="font-['Libre_Baskerville'] text-xs sm:text-sm md:text-base font-semibold text-[#1A2B3C]/70 leading-tight tracking-wide">
                  LIVING LAB
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-4 xl:gap-6" data-testid="desktop-nav">
              {mainNavItems.map((item) => (
                item.hasSubmenu ? (
                  <DropdownMenu key={item.slug}>
                    <DropdownMenuTrigger 
                      className="nav-link text-sm font-medium flex items-center gap-1" 
                      data-testid={`nav-${item.slug}-trigger`}
                    >
                      {item.name} <ChevronDown className="h-3 w-3" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="start" 
                      className="w-[420px] bg-white p-0 shadow-lg border border-[#1A2B3C]/10"
                    >
                      <div className="flex">
                        {/* Links Column */}
                        <div className="w-1/2 p-3 border-r border-[#1A2B3C]/10">
                          <DropdownMenuLabel className="text-xs text-[#1A2B3C]/50 font-normal px-2 mb-1">
                            {item.slug === 'workstations' ? t('nav_workspace_config') : item.name}
                          </DropdownMenuLabel>
                          {item.submenu.map((sub) => (
                            <DropdownMenuItem key={sub.slug} asChild>
                              <Link 
                                to={buildLink(`/category/${sub.slug}`)}
                                className="w-full cursor-pointer text-sm py-2 px-2 rounded-md hover:bg-[#F9F7F2]"
                                data-testid={`nav-${sub.slug}`}
                              >
                                {sub.name}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator className="my-2" />
                          <DropdownMenuItem asChild>
                            <Link 
                              to={buildLink(`/category/${item.slug}`)}
                              className="w-full cursor-pointer text-sm py-2 px-2 rounded-md text-[#2A9D8F] font-medium hover:bg-[#2A9D8F]/10"
                            >
                              {t('view_all')} {item.name} →
                            </Link>
                          </DropdownMenuItem>
                        </div>
                        {/* Featured Article */}
                        {item.featured && (
                          <div className="w-1/2 p-3">
                            <span className="text-xs text-[#1A2B3C]/50 px-1 block mb-2">Featured Guide</span>
                            <Link 
                              to={buildLink(`/blog/${item.featured.slug}`)}
                              className="block group"
                            >
                              <div className="aspect-[16/10] rounded-lg overflow-hidden mb-2 bg-[#F9F7F2]">
                                <img 
                                  src={item.featured.image} 
                                  alt={item.featured.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                              <h4 className="text-sm font-semibold text-[#1A2B3C] group-hover:text-[#2A9D8F] transition-colors line-clamp-2">
                                {item.featured.title}
                              </h4>
                            </Link>
                          </div>
                        )}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link 
                    key={item.slug}
                    to={item.slug === 'guides' ? buildLink("/blog") : buildLink(`/category/${item.slug}`)}
                    className={`nav-link text-sm font-medium ${isActive(`/category/${item.slug}`) ? 'text-[#1A2B3C]' : ''}`}
                    data-testid={`nav-${item.slug}`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </nav>

            {/* Desktop: Search + Language + CTA */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#1A2B3C]/70 hover:text-[#1A2B3C] transition-colors rounded-lg hover:bg-[#1A2B3C]/5"
                data-testid="search-toggle-desktop"
                aria-label={t('search')}
              >
                <Search className="h-4 w-4" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger 
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#1A2B3C]/70 hover:text-[#1A2B3C] transition-colors rounded-lg hover:bg-[#1A2B3C]/5"
                  data-testid="language-switcher-desktop"
                >
                  <Globe className="h-4 w-4" />
                  <span>{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44 bg-white">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`cursor-pointer ${language === lang.code ? 'bg-[#F9F7F2] font-semibold' : ''}`}
                      data-testid={`lang-desktop-${lang.code}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Link
                to={buildLink("/quiz")}
                className="text-sm font-medium text-[#2A9D8F] hover:text-[#238478] transition-colors"
              >
                Quiz
              </Link>
              <Button
                asChild
                className="bg-[#1A2B3C] text-white hover:bg-[#1A2B3C]/90 rounded-full px-5"
                data-testid="header-cta"
              >
                <Link to={buildLink("/blog")}>{t('read_more')}</Link>
              </Button>
            </div>

            {/* Mobile: Language Button + Menu Toggle */}
            <div className="flex lg:hidden items-center gap-1">
              {/* Mobile Language Button */}
              <button
                onClick={() => setLangSheetOpen(true)}
                className="flex items-center gap-1 px-2 py-2 text-sm font-medium text-[#1A2B3C]/70 hover:text-[#1A2B3C] transition-colors rounded-lg hover:bg-[#1A2B3C]/5 min-w-[44px] min-h-[44px] justify-center"
                data-testid="language-switcher-mobile"
                aria-label="Change language"
              >
                <Globe className="h-5 w-5" />
                <span className="text-xs font-semibold">{currentLang.code.toUpperCase()}</span>
              </button>

              {/* Mobile Menu Toggle */}
              <button
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-[#1A2B3C]" />
                ) : (
                  <Menu className="h-6 w-6 text-[#1A2B3C]" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Expandable Search Bar - Desktop */}
        {searchOpen && (
          <div className="hidden lg:block border-t border-[#1A2B3C]/10 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#1A2B3C]/40" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('search') + "..."}
                    className="w-full pl-12 pr-4 py-3 bg-[#F9F7F2] border border-[#1A2B3C]/10 rounded-full text-[#1A2B3C] placeholder-[#1A2B3C]/40 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/30 focus:border-[#2A9D8F]"
                    autoFocus
                    data-testid="search-input-desktop"
                  />
                </div>
                <Button 
                  type="submit"
                  className="bg-[#2A9D8F] text-white hover:bg-[#2A9D8F]/90 rounded-full px-6"
                  data-testid="search-submit-desktop"
                >
                  {t('search')}
                </Button>
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="text-[#1A2B3C]/60 hover:text-[#1A2B3C] px-2"
                  data-testid="search-close-desktop"
                >
                  <X className="h-5 w-5" />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden bg-[#F9F7F2] border-t border-[#1A2B3C]/5 max-h-[calc(100vh-68px)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-1">
              {/* Mobile Search */}
              <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#1A2B3C]/40" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('search') + "..."}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-[#1A2B3C]/10 rounded-xl text-[#1A2B3C] placeholder-[#1A2B3C]/40 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/30 focus:border-[#2A9D8F]"
                    data-testid="search-input-mobile"
                  />
                </div>
              </form>

              {/* Main Nav Items */}
              {mainNavItems.map((item) => (
                <div key={item.slug}>
                  {item.hasSubmenu ? (
                    <div className="py-2">
                      <span className="px-2 text-[#1A2B3C] text-sm font-semibold">{item.name}</span>
                      <div className="mt-2 space-y-1 ml-2">
                        {item.submenu.map((sub) => (
                          <Link
                            key={sub.slug}
                            to={buildLink(`/category/${sub.slug}`)}
                            className="flex items-center justify-between py-2 px-3 text-[#1A2B3C]/70 text-sm rounded-lg hover:bg-[#1A2B3C]/5 min-h-[44px]"
                            onClick={() => setMobileMenuOpen(false)}
                            data-testid={`mobile-nav-${sub.slug}`}
                          >
                            {sub.name}
                            <ChevronRight className="h-4 w-4 text-[#1A2B3C]/30" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link 
                      to={item.slug === 'guides' ? buildLink("/blog") : buildLink(`/category/${item.slug}`)}
                      className="block py-3 px-2 text-[#1A2B3C] font-medium rounded-lg hover:bg-[#1A2B3C]/5 min-h-[44px] flex items-center"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.slug}`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              {/* Quiz */}
              <Link
                to={buildLink("/quiz")}
                className="block py-3 px-2 text-[#2A9D8F] font-medium rounded-lg hover:bg-[#2A9D8F]/10 min-h-[44px] flex items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Find Your Gear →
              </Link>

              {/* Divider */}
              <div className="border-t border-[#1A2B3C]/10 my-2" />

              <Link
                to={buildLink("/about")}
                className="block py-3 px-2 text-[#1A2B3C] font-medium rounded-lg hover:bg-[#1A2B3C]/5 min-h-[44px] flex items-center"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-about"
              >
                {t('about')}
              </Link>

              <Link 
                to={buildLink("/contact")}
                className="block py-3 px-2 text-[#1A2B3C] font-medium rounded-lg hover:bg-[#1A2B3C]/5 min-h-[44px] flex items-center"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-contact"
              >
                {t('contact')}
              </Link>

              <div className="pt-4">
                <Button 
                  asChild
                  className="w-full bg-[#1A2B3C] text-white hover:bg-[#1A2B3C]/90 rounded-full min-h-[48px]"
                  data-testid="mobile-cta"
                >
                  <Link to={buildLink("/blog")} onClick={() => setMobileMenuOpen(false)}>
                    {t('read_more')}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Language Bottom Sheet */}
      {langSheetOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/40 z-[60] lg:hidden"
            onClick={() => setLangSheetOpen(false)}
            data-testid="lang-sheet-backdrop"
          />
          
          {/* Bottom Sheet */}
          <div 
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[70] lg:hidden animate-in slide-in-from-bottom duration-300"
            data-testid="language-bottom-sheet"
          >
            <div className="p-4">
              {/* Handle */}
              <div className="w-10 h-1 bg-[#1A2B3C]/20 rounded-full mx-auto mb-4" />
              
              <h3 className="text-lg font-semibold text-[#1A2B3C] text-center mb-4">
                {t('select_language')}
              </h3>
              
              <div className="grid grid-cols-1 gap-2">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`flex items-center gap-3 p-4 rounded-xl transition-colors min-h-[56px] ${
                      language === lang.code 
                        ? 'bg-[#1A2B3C] text-white' 
                        : 'bg-[#F9F7F2] text-[#1A2B3C] hover:bg-[#1A2B3C]/10'
                    }`}
                    data-testid={`lang-sheet-${lang.code}`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                    {language === lang.code && (
                      <span className="ml-auto text-xs bg-white/20 px-2 py-1 rounded-full">
                        {t('active')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setLangSheetOpen(false)}
                className="w-full mt-4 py-3 text-[#1A2B3C]/60 font-medium"
                data-testid="lang-sheet-cancel"
              >
                {t('cancel')}
              </button>
            </div>
            
            {/* Safe area padding for iPhone */}
            <div className="h-6" />
          </div>
        </>
      )}
    </>
  );
};

export default Header;
